/* ============================================================================
   LUXURY PDP — Interactivity
   - Gallery thumbnail switching
   - Variant picker (tiles + chips): updates price, variant id, URL, media
   - Description Read more / Read less
   - Add to Bag text-only states: idle → "Adding..." → "Added ✓"
   - Sticky mobile ATC: appears when inline CTA leaves viewport, proxies submit
   ============================================================================ */

(function () {
  if (customElements.get('luxury-product')) return;

  // Best-effort color-name -> hex map for the dynamic multi-option picker's
  // swatch dots. Shopify doesn't expose merchant-configured swatch colors
  // through `options_with_values` in Liquid, so we resolve a reasonable
  // color from the option VALUE text itself (e.g. "Olive wood" -> olive).
  // Unknown values simply get no swatch fill (the text label still shows).
  var COLOR_SWATCH_MAP = {
    black: '#111111', white: '#F5F5F0', ivory: '#FFFFF0', cream: '#F3EAD3',
    red: '#C0392B', burgundy: '#5C1A2B', maroon: '#5C1A2B', wine: '#5C1A2B',
    pink: '#E8A0BF', rose: '#C48793', blush: '#EAC6C0',
    orange: '#D97B29', peach: '#F2C29B', coral: '#E08A6B',
    yellow: '#E8C547', gold: '#C9A55A', mustard: '#C9A227',
    green: '#3E6B4F', olive: '#6B6B3A', emerald: '#1E6B4A', mint: '#A6D8C0', khaki: '#8A8A5C',
    blue: '#2E5D8A', navy: '#1B2A4A', teal: '#1F6F73', turquoise: '#3EA6A0', sky: '#8FC3E0', denim: '#3B5A78',
    purple: '#6B4A8A', lavender: '#B7A6D9', violet: '#7A5EA8', lilac: '#C9B6E4', plum: '#5C3A5C',
    brown: '#6B4A32', tan: '#C8A97A', beige: '#D9C7A8', taupe: '#B0A08A', wood: '#6B4A32', chocolate: '#3E2A1E',
    grey: '#8C8C8C', gray: '#8C8C8C', silver: '#C9C9C9', charcoal: '#3A3A3A',
    copper: '#B5652B', bronze: '#8C6B3F'
  };

  function resolveSwatchColor(name) {
    var key = String(name || '').toLowerCase().trim();
    if (!key) return null;
    if (COLOR_SWATCH_MAP[key]) return COLOR_SWATCH_MAP[key];
    // Multi-word values (e.g. "Olive wood", "Rose gold"): try each word,
    // last word first - it's usually the qualifier/base tone.
    var words = key.split(/\s+/);
    for (var i = words.length - 1; i >= 0; i--) {
      if (COLOR_SWATCH_MAP[words[i]]) return COLOR_SWATCH_MAP[words[i]];
    }
    return null;
  }

  class LuxuryProduct extends HTMLElement {
    connectedCallback() {
      this.variants = this.parseVariants();
      this.currentVariant = this.variants.find(v => String(v.id) === this.dataset.variantId) || this.variants[0];

      this.initGallery();
      this.initDescription();
      this.initAtc();
      this.initNotify();
      this.initVariantPicker();
      this.initSwatches();
      this.initStickyBar();
    }

    parseVariants() {
      const tag = this.querySelector('[data-luxury-variants]');
      if (!tag) return [];
      try { return JSON.parse(tag.textContent); } catch (e) { return []; }
    }

    /* ──────────── Gallery ──────────── */
    initGallery() {
      this.slides = Array.from(this.querySelectorAll('[data-luxury-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-luxury-thumb]'));
      this.dots   = Array.from(this.querySelectorAll('[data-luxury-dot]'));
      this.stage  = this.querySelector('[data-luxury-stage]');

      if (this.slides.length < 2) return;

      // Thumb clicks (desktop)
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.activateSlide(thumb.dataset.mediaId));
      });

      // Dot clicks (mobile)
      this.dots.forEach((dot) => {
        dot.style.pointerEvents = 'auto';
        dot.style.cursor = 'pointer';
        dot.setAttribute('role', 'button');
        dot.setAttribute('tabindex', '0');
        dot.setAttribute('aria-label', 'Show image');
        const fire = () => this.activateSlide(dot.dataset.mediaId);
        dot.addEventListener('click', fire);
        dot.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fire(); }
        });
      });

      // Touch swipe (mobile)
      if (this.stage) {
        let startX = 0, startY = 0, dx = 0, dy = 0, tracking = false;
        const isRTL = document.documentElement.dir === 'rtl';

        this.stage.addEventListener('touchstart', (e) => {
          if (!e.touches[0]) return;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          dx = dy = 0;
          tracking = true;
        }, { passive: true });

        this.stage.addEventListener('touchmove', (e) => {
          if (!tracking || !e.touches[0]) return;
          dx = e.touches[0].clientX - startX;
          dy = e.touches[0].clientY - startY;
        }, { passive: true });

        this.stage.addEventListener('touchend', () => {
          if (!tracking) return;
          tracking = false;
          if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return; // ignore taps / vertical scrolls
          const forward = isRTL ? dx > 0 : dx < 0;
          this.advanceSlide(forward ? 1 : -1);
        });
      }
    }

    advanceSlide(delta) {
      const idx = this.slides.findIndex(s => s.classList.contains('is-active'));
      if (idx < 0) return;
      const next = (idx + delta + this.slides.length) % this.slides.length;
      this.activateSlide(this.slides[next].dataset.mediaId);
    }

    activateSlide(mediaId) {
      if (!mediaId) return;
      this.slides.forEach((s) => {
        const on = s.dataset.mediaId === String(mediaId);
        s.classList.toggle('is-active', on);
        if (on) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
      });
      this.thumbs.forEach((t) => {
        const on = t.dataset.mediaId === String(mediaId);
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.dots.forEach((d) => d.classList.toggle('is-active', d.dataset.mediaId === String(mediaId)));
    }

    /* ──────────── Description ──────────── */
    /*
     * Safari-safe overflow detection.
     *
     * The previous version measured `scrollHeight` synchronously inside
     * connectedCallback(), i.e. the instant the custom element upgrades.
     * On a real iPhone that happens before the display webfont
     * (Cormorant Garamond) has finished loading, so Safari lays the text
     * out with a fallback font whose metrics differ enough that the
     * "full" and "clamped" heights come out equal — the toggle then gets
     * hidden forever, even though the final (webfont) text truly
     * overflows 3 lines. Chrome's responsive-mode emulator doesn't
     * reproduce this because the font is already loaded/cached.
     *
     * Fix: don't trust a single measurement taken at connect time. Wait
     * for fonts + layout to settle (document.fonts.ready, window `load`,
     * and a double rAF to flush any pending reflow), then measure. Keep
     * re-measuring on resize/orientation change and on any size change of
     * the description body itself (ResizeObserver), since line-wrapping
     * depends on the container width.
     */
    initDescription() {
      const body   = this.querySelector('[data-luxury-description-body]');
      const toggle = this.querySelector('[data-luxury-description-toggle]');
      const label  = this.querySelector('[data-luxury-description-toggle-label]');
      if (!body || !toggle || !label) return;

      const measureOverflow = () => {
        const wasCollapsed = body.getAttribute('data-collapsed') === 'true';
        body.setAttribute('data-collapsed', 'false');
        const full = body.scrollHeight;
        body.setAttribute('data-collapsed', 'true');
        const clamped = body.clientHeight;
        body.setAttribute('data-collapsed', wasCollapsed ? 'true' : 'false');
        return (full - clamped) > 4;
      };

      const refresh = () => {
        // Never hide the toggle while the reader has it expanded — only
        // decide visibility while collapsed, and only once metrics can be
        // trusted (see notes above).
        if (body.getAttribute('data-collapsed') !== 'true') return;
        toggle.hidden = !measureOverflow();
      };

      const scheduleRefresh = () => {
        // Double rAF: first frame lets any pending style/font work commit,
        // second frame reads the now-settled box metrics.
        requestAnimationFrame(() => requestAnimationFrame(refresh));
      };

      // Hide by default until a trustworthy measurement lands, so we never
      // flash an incorrect toggle state while fonts are still swapping in.
      toggle.setAttribute('hidden', '');

      const fontsReady = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
      fontsReady.then(scheduleRefresh, scheduleRefresh);

      if (document.readyState === 'complete') {
        scheduleRefresh();
      } else {
        window.addEventListener('load', scheduleRefresh, { once: true });
      }

      let resizeTimer;
      const debouncedRefresh = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(scheduleRefresh, 150);
      };
      window.addEventListener('resize', debouncedRefresh);
      window.addEventListener('orientationchange', debouncedRefresh);

      if ('ResizeObserver' in window) {
        new ResizeObserver(debouncedRefresh).observe(body);
      }

      toggle.addEventListener('click', () => {
        const collapsed = body.getAttribute('data-collapsed') === 'true';
        body.setAttribute('data-collapsed', collapsed ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', collapsed ? 'true' : 'false');
        label.textContent = collapsed ? 'Read less' : 'Read more';
        if (!collapsed) {
          // Just collapsed again — re-check whether it's still overflowing.
          scheduleRefresh();
        }
      });
    }

    /* ──────────── Add to Bag ──────────── */
    initAtc() {
      this.form   = this.querySelector('form[data-type="add-to-cart-form"]');
      this.atc    = this.querySelector('[data-luxury-atc]');
      this.atcLbl = this.querySelector('[data-luxury-atc-label]');
      if (!this.form || !this.atc || !this.atcLbl) return;

      this.atcLabels = {
        idle:    this.atc.dataset.labelIdle    || 'Add to Bag',
        loading: this.atc.dataset.labelLoading || 'Adding...',
        success: this.atc.dataset.labelSuccess || 'Added ✓',
        soldout: this.atc.dataset.labelSoldout || 'Sold Out',
      };

      this.atc.addEventListener('click', () => {
        if (this.atc.disabled) return;
        this.atc.dataset.state = 'loading';
        this.atcLbl.textContent = this.atcLabels.loading;
      });

      document.addEventListener('cart:added', () => this.markAtcSuccess());
      document.addEventListener('cart-drawer:open', () => this.markAtcSuccess());

      // Fallback: watch class toggling on the button
      new MutationObserver(() => {
        if (this.atc.dataset.state === 'loading' && !this.atc.classList.contains('loading') && !this.atc.disabled) {
          this.markAtcSuccess();
        }
      }).observe(this.atc, { attributes: true, attributeFilter: ['class', 'disabled'] });

      // Error revert
      const err = this.querySelector('.luxury-pdp__form-error');
      if (err) {
        new MutationObserver(() => {
          if (!err.hasAttribute('hidden')) this.resetAtc();
        }).observe(err, { attributes: true, attributeFilter: ['hidden'] });
      }
    }

    markAtcSuccess() {
      if (this.atc.dataset.state === 'success') return;
      this.atc.dataset.state = 'success';
      this.atcLbl.textContent = this.atcLabels.success;
      clearTimeout(this._atcResetTimer);
      this._atcResetTimer = setTimeout(() => this.resetAtc(), 2000);
    }

    resetAtc() {
      delete this.atc.dataset.state;
      this.atcLbl.textContent = this.currentVariant && this.currentVariant.available ? this.atcLabels.idle : this.atcLabels.soldout;
    }

    /* ──────────── Notify Me (sold-out fallback) ──────────── */
    initNotify() {
      this.notify = this.querySelector('[data-luxury-notify]');
      if (!this.notify) return;
      this.notify.addEventListener('click', () => {
        // Dispatch a custom event so a future modal/newsletter can listen.
        this.dispatchEvent(new CustomEvent('luxury:notify-request', {
          bubbles: true,
          detail: { productId: this.dataset.productId, variantId: this.dataset.variantId }
        }));
        // Fallback UX: scroll to a #notify anchor or footer newsletter
        const target = document.querySelector('#notify, #newsletter, .newsletter');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    /* ──────────── Variant Picker ──────────── */
    initVariantPicker() {
      // Legacy single-option pickers (tiles/chips): each value maps 1:1 to a
      // variant id already baked into the markup — unchanged behavior.
      const singleInputs = Array.from(this.querySelectorAll('[data-luxury-tile-input], [data-luxury-chip-input]'));
      singleInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          const variant = this.variants.find(v => String(v.id) === String(input.dataset.variantId));
          if (variant) this.selectVariant(variant);
        });
      });

      // Dynamic multi-option picker: any number of Shopify options (2–3),
      // each with any number of values. The variant is resolved by matching
      // the full combination of currently selected values, not one input.
      this.optionInputs = Array.from(this.querySelectorAll('[data-luxury-option-input]'));
      if (this.optionInputs.length === 0) return;

      // Seed from the initially selected/first-available variant so every
      // option position has a value before the shopper interacts.
      this.selectedOptions = (this.currentVariant && this.currentVariant.options)
        ? this.currentVariant.options.slice()
        : [];

      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          const index = Number(input.dataset.optionIndex);
          this.selectedOptions[index] = input.value;
          this.applyOptionSelection();
        });
      });

      this.applyOptionSelection();
    }

    // Does `variant.options` match `this.selectedOptions`, with `overrides`
    // substituted at specific indexes (used to test "what if" combinations
    // for a single changed value without mutating the real selection)?
    optionsMatch(variant, overrides) {
      if (!variant.options) return false;
      return variant.options.every((val, i) => {
        const want = (overrides && overrides[i] !== undefined) ? overrides[i] : this.selectedOptions[i];
        return want === undefined || val === want;
      });
    }

    // Re-derive the whole multi-option picker UI from `this.selectedOptions`:
    // per-option legend readouts, which values are still valid/available
    // combinations (so shoppers can only pick S/M combos that actually
    // exist), and the resulting variant (price/media/availability/ATC).
    applyOptionSelection() {
      this.querySelectorAll('[data-luxury-option-group]').forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const readout = group.querySelector('[data-luxury-picker-value]');
        if (readout && this.selectedOptions[index] !== undefined) readout.textContent = this.selectedOptions[index];
      });

      this.optionInputs.forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const overrides = this.selectedOptions.slice();
        overrides[index] = input.value;
        const candidate = this.variants.find(v => this.optionsMatch(v, overrides));
        const exists = !!candidate;
        const available = !!(candidate && candidate.available);

        input.checked = this.selectedOptions[index] === input.value;
        input.disabled = !exists;
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('is-soldout', exists && !available);
      });

      const variant = this.variants.find(v => this.optionsMatch(v));
      if (variant) {
        this.selectVariant(variant);
      } else {
        this.markUnavailableCombo();
      }
    }

    // Safety net for partial variant matrices: no variant exists for the
    // exact combination currently selected. Disable Add to Bag rather than
    // submit a stale/incorrect variant id.
    markUnavailableCombo() {
      const hidden = this.querySelector('[data-luxury-variant-id]');
      if (hidden) hidden.setAttribute('disabled', '');
      [this.atc, this.querySelector('[data-luxury-sticky-atc]')].forEach((btn) => {
        if (btn) btn.setAttribute('disabled', '');
      });
      if (this.atcLbl) this.atcLbl.textContent = this.atcLabels.soldout;
      const stickyLbl = this.querySelector('[data-luxury-sticky-atc-label]');
      if (stickyLbl) stickyLbl.textContent = this.atcLabels.soldout;
    }

    /* ──────────── Color swatches (multi-option picker) ──────────── */
    initSwatches() {
      this.querySelectorAll('[data-luxury-swatch]').forEach((el) => {
        const color = resolveSwatchColor(el.dataset.colorName);
        if (color) el.style.backgroundColor = color;
      });
    }

    selectVariant(variant) {
      this.currentVariant = variant;
      this.dataset.variantId = variant.id;

      // 1. Hidden variant input (drives Add to Bag)
      const hidden = this.querySelector('[data-luxury-variant-id]');
      if (hidden) {
        hidden.value = variant.id;
        if (variant.available) hidden.removeAttribute('disabled'); else hidden.setAttribute('disabled', '');
      }

      // 2. Price elements (main + sticky)
      this.querySelectorAll('[data-luxury-price], [data-luxury-sticky-price]').forEach(el => {
        el.textContent = variant.price_display;
      });

      // 3. Chip mode value readout (single-option products only — the
      //    multi-option picker updates its own per-option legend in
      //    applyOptionSelection()).
      if (!this.optionInputs || this.optionInputs.length === 0) {
        const pickerValue = this.querySelector('[data-luxury-picker-value]');
        if (pickerValue) pickerValue.textContent = variant.option1;
      }

      // 4. ATC state
      const soldout = !variant.available;
      [this.atc, this.querySelector('[data-luxury-sticky-atc]')].forEach(btn => {
        if (!btn) return;
        if (soldout) btn.setAttribute('disabled', ''); else btn.removeAttribute('disabled');
      });
      if (this.atcLbl) this.atcLbl.textContent = soldout ? this.atcLabels.soldout : this.atcLabels.idle;
      const stickyLbl = this.querySelector('[data-luxury-sticky-atc-label]');
      if (stickyLbl) stickyLbl.textContent = soldout ? this.atcLabels.soldout : this.atcLabels.idle;

      // 4b. Notify Me visibility
      if (this.notify) {
        if (soldout) this.notify.removeAttribute('hidden'); else this.notify.setAttribute('hidden', '');
      }

      // 5. URL
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* noop */ }

      // 6. Media swap (if variant has a featured media)
      if (variant.featured_media_id) {
        this.activateSlide(variant.featured_media_id);
      }
    }

    /* ──────────── Sticky Mobile ATC ──────────── */
    initStickyBar() {
      this.sticky      = this.querySelector('[data-luxury-sticky]');
      this.stickyAtc   = this.querySelector('[data-luxury-sticky-atc]');
      if (!this.sticky || !this.atc) return;

      // Proxy click to main ATC submit
      if (this.stickyAtc) {
        this.stickyAtc.addEventListener('click', () => {
          if (this.stickyAtc.disabled) return;
          this.atc.click();
        });
      }

      // Reveal when inline CTA leaves the viewport
      const io = new IntersectionObserver((entries) => {
        const entry = entries[0];
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-visible', visible);
        if (visible) this.sticky.removeAttribute('aria-hidden'); else this.sticky.setAttribute('aria-hidden', 'true');
      }, { threshold: 0, rootMargin: '0px' });
      io.observe(this.atc);
    }
  }

  customElements.define('luxury-product', LuxuryProduct);

  /* ════════════════════════════════════════════════════════════
     Page-level enhancers (outside <luxury-product>)
     - Description Read-more (handles `data-luxury-description-full`)
     - Section reveal-on-scroll
     - Recommendations: arrows + auto-fetch fallback
     - FAQ smooth animation (CSS handles via grid-rows; nothing to do)
     ════════════════════════════════════════════════════════════ */

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ───── Description Read-more (full metafield variant) ───── */
  function initDescriptionFull() {
    document.querySelectorAll('[data-luxury-description]').forEach((wrap) => {
      const body   = wrap.querySelector('[data-luxury-description-body]');
      const full   = wrap.querySelector('[data-luxury-description-full]');
      const toggle = wrap.querySelector('[data-luxury-description-toggle]');
      const label  = wrap.querySelector('[data-luxury-description-toggle-label]');
      if (!toggle || !label || !full) return; // body-only handled by the custom element
      toggle.removeAttribute('hidden');
      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (expanded) {
          full.setAttribute('hidden', '');
          if (body) body.setAttribute('data-collapsed', 'true');
          label.textContent = 'Read more';
        } else {
          full.removeAttribute('hidden');
          if (body) body.setAttribute('data-collapsed', 'false');
          label.textContent = 'Read less';
        }
      });
    });
  }

  /* ───── Section reveal-on-scroll ───── */
  function initSectionReveal() {
    const sections = document.querySelectorAll('.luxury-section');
    if (!sections.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      sections.forEach((s) => s.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -8% 0px' });
    sections.forEach((s) => io.observe(s));
  }

  /* ───── Recommendations: arrows + auto-fetch fallback ───── */
  function initRecs() {
    document.querySelectorAll('[data-luxury-recs]').forEach((recs) => {
      const track = recs.querySelector('[data-luxury-recs-track]');
      const prev  = recs.querySelector('[data-luxury-recs-prev]');
      const next  = recs.querySelector('[data-luxury-recs-next]');
      if (!track) return;

      const loading = track.querySelector('[data-luxury-recs-loading]');
      const url     = recs.dataset.recsUrl;
      if (loading && url) {
        fetch(url)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const incoming = doc.querySelector('[data-luxury-recs-track]');
            if (incoming && incoming.innerHTML.trim()) {
              track.innerHTML = incoming.innerHTML;
            } else {
              recs.hidden = true;
            }
          })
          .catch(() => { recs.hidden = true; })
          .finally(() => updateArrows());
      }

      const cardGap = () => {
        const cs = getComputedStyle(track);
        return parseFloat(cs.columnGap || cs.gap || '0') || 0;
      };
      const step = () => {
        const card = track.querySelector('.luxury-card');
        if (!card) return track.clientWidth * 0.8;
        return card.getBoundingClientRect().width + cardGap();
      };
      const isRtl = () => getComputedStyle(track).direction === 'rtl';
      const updateArrows = () => {
        if (!prev || !next) return;
        // In RTL, scrollLeft is negative in modern browsers and starts at 0
        // when the first (right-most) card is visible. Use abs() so the
        // start/end detection works for both directions.
        const sl       = Math.abs(track.scrollLeft);
        const atStart  = sl <= 4;
        const atEnd    = sl + track.clientWidth >= track.scrollWidth - 4;
        prev.toggleAttribute('disabled', atStart);
        next.toggleAttribute('disabled', atEnd);
        const overflow = track.scrollWidth > track.clientWidth + 4;
        recs.classList.toggle('luxury-recs--has-overflow', overflow);
      };

      // In RTL the visual "next" must move toward more-negative scrollLeft.
      if (prev) prev.addEventListener('click', () => {
        const dir = isRtl() ? 1 : -1;
        track.scrollBy({ left: dir * step(), behavior: reduced ? 'auto' : 'smooth' });
      });
      if (next) next.addEventListener('click', () => {
        const dir = isRtl() ? -1 : 1;
        track.scrollBy({ left: dir * step(), behavior: reduced ? 'auto' : 'smooth' });
      });
      track.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows);
      // initial after layout
      requestAnimationFrame(updateArrows);
    });
  }

  function boot() {
    initDescriptionFull();
    initSectionReveal();
    initRecs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();